/**
 * Offline Field Mode (Phase 51) — device-side storage + sync engine.
 *
 * - "pack": a per-tenant bundle (masters, assigned properties, demands) downloaded
 *   while online so Survey / Reassessment / Visit flows keep working without network.
 * - "queue": actions captured offline (surveys, reassessments, visits, mark-visited)
 *   that are replayed against the normal APIs when connectivity returns.
 *
 * Sync is idempotent: every queued action carries a client_ref UUID the backend
 * uses to de-duplicate replays.
 */
import { useEffect, useState } from "react";
import { api } from "./api";

const DB_NAME = "mc_offline";
const DB_VERSION = 1;
export const QUEUE_EVENT = "mc-queue-changed";

// ---------- tiny IndexedDB promise wrapper ----------
let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("pack")) db.createObjectStore("pack");
      if (!db.objectStoreNames.contains("queue")) db.createObjectStore("queue", { keyPath: "id" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function idbGet(store, key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const r = db.transaction(store).objectStore(store).get(key);
    r.onsuccess = () => resolve(r.result ?? null);
    r.onerror = () => reject(r.error);
  });
}

async function idbPut(store, value, key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, "readwrite");
    key === undefined ? tx.objectStore(store).put(value) : tx.objectStore(store).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function idbDel(store, key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, "readwrite");
    tx.objectStore(store).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function idbAll(store) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const r = db.transaction(store).objectStore(store).getAll();
    r.onsuccess = () => resolve(r.result || []);
    r.onerror = () => reject(r.error);
  });
}

// ---------- helpers ----------
export const newClientRef = () =>
  (window.crypto?.randomUUID ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

export const isNetworkError = (err) => !!err && !err.response;

const emitQueueChanged = () => window.dispatchEvent(new Event(QUEUE_EVENT));

// ---------- offline pack ----------
export async function downloadPack(code) {
  const r = await api.get(`/t/${code}/field/offline-pack`);
  await idbPut("pack", { code, ...r.data }, code);
  return r.data;
}

export async function getPack(code) {
  try { return await idbGet("pack", code); } catch { return null; }
}

// ---------- offline user manual (Phase 78) ----------
// Cached in the same "pack" store under a namespaced key so no IndexedDB version bump is needed.
const manualKey = (code) => `manual:${code}`;

/** Fetch the role-filtered manual while online and save it on the device. */
export async function downloadManual(code) {
  const r = await api.get(`/t/${code}/manual`);
  const doc = { code, ...r.data, cached_at: new Date().toISOString() };
  await idbPut("pack", doc, manualKey(code));
  return doc;
}

/** Manual saved on this device (or null if never opened online). */
export async function getCachedManual(code) {
  try { return await idbGet("pack", manualKey(code)); } catch { return null; }
}

// ---------- queue ----------
/** kinds: "survey" | "reassessment" | "visit" | "mark_visited" | "mobile_verify" (Phase 134) */
export async function queueAdd(code, kind, payload, extras = {}) {
  const item = {
    id: newClientRef(), code, kind, payload,
    photos: extras.photos || [],          // [{data: dataURL, filename, lat?, lng?, accuracy?, captured_at?, client_ref?}] (Phase 132: GPS rides along)
    calcInputs: extras.calcInputs || null, // {fyIds, floors, road_width_id, owner_name}
    label: extras.label || "",
    created_at: new Date().toISOString(),
    status: "pending", error: null,
  };
  await idbPut("queue", item);
  emitQueueChanged();
  return item;
}

export async function listQueue(code) {
  const all = await idbAll("queue");
  return all.filter((i) => i.code === code)
    .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
}

export async function removeQueueItem(id) {
  await idbDel("queue", id);
  emitQueueChanged();
}

async function markItemFailed(item, message) {
  await idbPut("queue", { ...item, status: "failed", error: message });
  emitQueueChanged();
}

// ---------- hooks ----------
export function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);
  return online;
}

export function useQueue(code) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let alive = true;
    const refresh = () => listQueue(code).then((q) => { if (alive) setItems(q); }).catch(() => {});
    refresh();
    window.addEventListener(QUEUE_EVENT, refresh);
    return () => { alive = false; window.removeEventListener(QUEUE_EVENT, refresh); };
  }, [code]);
  return items;
}

// ---------- sync engine ----------
const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(", ");
  return e?.message || "Sync failed";
};

/** Replicates the silent Rate Engine calculation done by the online forms. */
async function computeCalc(code, calcInputs) {
  const { fyIds = [], floors = [], road_width_id = null, owner_name = null } = calcInputs || {};
  const valid = (floors || []).filter((f) => Number(f.area_sqft) > 0 && f.usage_type_id);
  if (!fyIds.length || !valid.length) return null;
  const vacantArea = (floors || []).reduce((s, f) => s + (Number(f.open_area_sqft) || 0), 0);
  const mkBody = (fid) => ({
    financial_year_id: fid, road_width_id: road_width_id || null,
    vacant_area_sqft: vacantArea || null,
    floors: valid.map((f) => ({
      floor_label: f.floor_label, owner_name: owner_name || null,
      usage_type_id: f.usage_type_id, area_sqft: Number(f.area_sqft),
      carpet_area_sqft: Number(f.carpet_area_sqft) || null,
      const_type_id: f.const_type_id || null,
      fee_category_id: f.fee_category_id || null, fee_sub_category_id: f.fee_sub_category_id || null,
    })),
  });
  const results = [];
  for (const fid of fyIds) {
    const r = await api.post(`/t/${code}/rate-engine/calculate`, mkBody(fid));
    results.push(r.data);
  }
  const first = results[0];
  if (fyIds.length === 1) return first;
  return {
    ...first, multi_year: true,
    per_year: results.map((r) => ({ financial_year_id: r.financial_year_id, fy_label: r.fy_label,
      arv_total_yearly: r.arv_total_yearly, total_yearly: r.grand_total?.total_yearly || 0 })),
    proposed_demands: results.flatMap((r) => r.proposed_demands || []),
    grand_total: { total_yearly: Math.round(results.reduce((s, r) => s + (r.grand_total?.total_yearly || 0), 0) * 100) / 100 },
    warnings: [...new Set(results.flatMap((r) => r.warnings || []))],
  };
}

async function uploadQueuedPhotos(code, photos) {
  const urls = [];
  for (const p of photos || []) {
    // Phase 132: field captures carry the device fix; `purpose` makes the server enforce it.
    // (Items queued by older builds may lack GPS — they still sync, without the purpose flag.)
    const gps = p.lat != null && p.lng != null ? { lat: p.lat, lng: p.lng, accuracy: p.accuracy ?? null, purpose: "field_photo", captured_at: p.captured_at || null } : {};
    const r = await api.post(`/t/${code}/files/photo`, { data: p.data, filename: p.filename || "offline-photo.jpg", ...gps });
    urls.push(r.data.url);
  }
  return urls;
}

const dataUrlToBlob = async (dataUrl) => (await fetch(dataUrl)).blob();

/** Phase 132: GPS photos captured in the Record Visit drawer while offline -> property photos (same
 *  client_ref => the server returns the already-stored photo, so a retried sync never duplicates). */
async function uploadQueuedVisitPhotos(code, propertyId, photos) {
  const ids = [];
  for (const p of photos || []) {
    if (!p.data) continue;
    const fd = new FormData();
    fd.append("file", await dataUrlToBlob(p.data), p.filename || "visit-photo.jpg");
    fd.append("lat", String(p.lat));
    fd.append("lng", String(p.lng));
    if (p.accuracy != null) fd.append("accuracy", String(p.accuracy));
    if (p.captured_at) fd.append("captured_at", p.captured_at);
    if (p.client_ref) fd.append("client_ref", p.client_ref);
    const r = await api.post(`/t/${code}/field/property-photos/${propertyId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    ids.push(r.data.photo.file_id);
  }
  return ids;
}

async function processItem(code, item) {
  const payload = { ...item.payload, client_ref: item.id };
  if (item.kind === "mobile_verify") {
    // Phase 134: owner mobile confirm / update done offline — replays FIRST so the visit / mark-visited
    // that followed it on the device passes the server-side verification gate
    const { property_id, ...body } = payload;
    await api.post(`/t/${code}/properties/${property_id}/mobile/verify`, body);
    return;
  }
  if (item.kind === "mark_visited") {
    await api.put(`/t/${code}/field/mark-visited`, payload);
    return;
  }
  if (item.kind === "visit") {
    const ids = await uploadQueuedVisitPhotos(code, payload.property_id, item.photos);
    payload.photo_file_ids = [...(payload.photo_file_ids || []), ...ids];
    await api.post(`/t/${code}/field/visit-log`, payload);
    return;
  }
  // surveys + reassessments: upload photos first, then compute demands, then submit
  const photoUrls = await uploadQueuedPhotos(code, item.photos);
  payload.photos = [...(payload.photos || []), ...photoUrls];
  let calcResult = null;
  if (item.calcInputs && !payload.retained_as_is) {
    calcResult = await computeCalc(code, item.calcInputs);
  }
  if (calcResult) {
    payload.assessment = calcResult;
    payload.proposed_demands = calcResult.proposed_demands || [];
    if (item.kind === "reassessment") {
      payload.super_builtup_area_sqft = calcResult.total_constructed_area_sqft;
      payload.carpet_area_sqft = calcResult.carpet_area_sqft;
    }
  }
  if (item.kind === "survey") {
    try {
      const s = await api.post(`/t/${code}/surveys/start`);
      payload.survey_id = s.data.survey_id;
    } catch (e) { if (isNetworkError(e)) throw e; /* non-fatal: server assigns an ID */ }
    await api.post(`/t/${code}/surveys`, payload);
    return;
  }
  if (item.kind === "reassessment") {
    try {
      const s = await api.post(`/t/${code}/reassessments/start`, { property_id: payload.property_id });
      payload.reassessment_id = s.data.reassessment_id;
    } catch (e) { if (isNetworkError(e)) throw e; }
    await api.post(`/t/${code}/reassessments`, payload);
    return;
  }
  throw new Error(`Unknown queue kind: ${item.kind}`);
}

// ---------- sync health heartbeat (Phase 52) ----------
/** Best-effort: report this device's queue health so supervisors can spot
 *  collectors with unsynced offline work. */
export async function reportSyncStatus(code) {
  if (!navigator.onLine) return;
  try {
    const q = await listQueue(code);
    const pending = q.filter((i) => i.status !== "failed");
    const failed = q.filter((i) => i.status === "failed");
    const kinds = {};
    q.forEach((i) => { kinds[i.kind] = (kinds[i.kind] || 0) + 1; });
    await api.post(`/t/${code}/field/sync-status`, {
      pending_count: pending.length,
      failed_count: failed.length,
      kinds,
      oldest_pending_at: q[0]?.created_at || null,
      labels: q.slice(0, 5).map((i) => i.label || i.kind),
    });
  } catch { /* best-effort — never disturb the user */ }
}

let reportTimer = null;
/** Debounced heartbeat — safe to call on every queue change. */
export function scheduleSyncStatusReport(code) {
  if (reportTimer) clearTimeout(reportTimer);
  reportTimer = setTimeout(() => { reportTimer = null; reportSyncStatus(code); }, 2500);
}

const KIND_ORDER = { mobile_verify: 0, mark_visited: 1, visit: 2, survey: 3, reassessment: 4 };
let syncing = false;

/** Replays the whole queue. Returns {synced, failed, remaining} (null if a sync is already running). */
export async function syncAll(code) {
  if (syncing) return null;
  syncing = true;
  const out = { synced: 0, failed: 0, remaining: 0 };
  try {
    const items = (await listQueue(code))
      .sort((a, b) => (KIND_ORDER[a.kind] - KIND_ORDER[b.kind]) || (a.created_at || "").localeCompare(b.created_at || ""));
    for (const item of items) {
      try {
        await processItem(code, item);
        await removeQueueItem(item.id);
        out.synced += 1;
      } catch (e) {
        if (isNetworkError(e)) { out.remaining += 1; break; } // connection dropped again — stop, retry later
        await markItemFailed(item, errText(e));
        out.failed += 1;
      }
    }
    // refresh the offline pack after a successful sync so cached data stays current
    if (out.synced > 0 && navigator.onLine) {
      try { await downloadPack(code); } catch { /* best-effort */ }
    }
  } finally {
    syncing = false;
    emitQueueChanged();
    // heartbeat: tell the server what's still on this device (Phase 52)
    reportSyncStatus(code);
  }
  return out;
}
